import { PrepackQuantityPipe } from '@epicuro-next/ataraxia/prepack-quantity-pipe';
import {
  IOrdersSettings,
  Order,
  OrderMaterial,
} from '@epicuro-next/models/order-book';
import { hashObject } from '@epicuro-next/utilities';

import { getOrderDetailParams } from '../../interceptors/order-details-api.interceptor';
import {
  IFormatMap,
  IOrderLine,
  IOrderMaterialLine,
  IOrderMaterialWithId,
  IOrderPipesMap,
  ListPageActions,
  OrderMaterialsAdditionalPropertiesKeys,
} from '../../models';
import {
  getDateValue,
  getFormattedOrderType,
  getNumberValue,
  getOrderQuantity,
  getOrderThumbnails,
} from '../get-value/get-value';

export function mapOrderMaterialsToOrderRows(
  orders: IOrderMaterialWithId[],
  settings: IOrdersSettings,
  pipes: IOrderPipesMap,
  formatMap: IFormatMap,
  locale: string,
): IOrderMaterialLine[] {
  return orders.map((order) =>
    mapOrderMaterialToOrderRow(order, settings, pipes, formatMap, locale),
  );
}

export function mapOrderMaterialToOrderRow(
  order: IOrderMaterialWithId,
  settings: IOrdersSettings,
  pipes: IOrderPipesMap,
  formatMap: IFormatMap,
  locale: string,
): IOrderMaterialLine {
  return {
    ...order,
    netPrice: {
      ...order.netPrice,
      unit: getNumberValue<number>(order.netPrice.unit, pipes.number),
      total: getNumberValue<number>(order.netPrice.total, pipes.number),
    },
    requestedDeliveryDate: getDateValue(
      order.requestedDeliveryDate,
      locale,
      order.orderType === 'future' ? formatMap.futureDeliveryFormat : undefined,
    ),
    estimatedTimeOfArrival: getDateValue(order.estimatedTimeOfArrival, locale),
    [OrderMaterialsAdditionalPropertiesKeys.Quantity]: getOrderQuantity(
      order,
      pipes?.prepackQuantity as PrepackQuantityPipe,
    ),
    [OrderMaterialsAdditionalPropertiesKeys.ImageUrl]: getOrderThumbnails(
      {
        productId: order.materials[0].productId,
        brandId: order.brandId,
      },
      settings,
    ),
    invoicesEnabled: Object.keys(order.invoices ?? {}).length > 0,
    trackingEnabled: order.deliveryInfo.length > 0,
    [OrderMaterialsAdditionalPropertiesKeys.OrderDetailsRedirectUrlParams]:
      getOrderDetailParams(order.accountId),
    [OrderMaterialsAdditionalPropertiesKeys.TrackingAction]: {
      type: ListPageActions.TrackingModal,
      value: order.deliveryInfo,
    },
    [OrderMaterialsAdditionalPropertiesKeys.InvoicesAction]: {
      type: ListPageActions.InvoicesModal,
      value: order.invoices,
    },
    [OrderMaterialsAdditionalPropertiesKeys.CSPath]: [
      'cs',
      order.brandId,
      order.productId,
      order.productId,
    ],
    [OrderMaterialsAdditionalPropertiesKeys.OrderTypeFormatted]:
      getFormattedOrderType(order.orderType),
  };
}

export function extendWithInternalId(
  orderMaterial: OrderMaterial,
): IOrderMaterialWithId {
  return {
    internalId: hashObject(orderMaterial) as string,
    ...orderMaterial,
  };
}

export function mapOrderToOrderRow(
  order: Order,
  pipes: IOrderPipesMap,
  locale: string,
): IOrderLine {
  return {
    ...order,
    internalId: order.orderNumber,
    placedDate: getDateValue(order.placedDate, locale),
    requestedDate: getDateValue(order.placedDate, locale),
    value: getNumberValue<string>(order.value, pipes.number),
    [OrderMaterialsAdditionalPropertiesKeys.OrderDetailsRedirectUrlParams]:
      getOrderDetailParams(order.accountId),
    [OrderMaterialsAdditionalPropertiesKeys.OrderTypeFormatted]:
      getFormattedOrderType(order.orderType),
    invoicesEnabled: Object.keys(order.invoices ?? {}).length > 0,
    [OrderMaterialsAdditionalPropertiesKeys.InvoicesAction]: {
      type: ListPageActions.InvoicesModal,
      value: order.invoices,
    },
  };
}
